import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import StoryForm, { StoryFormData } from '../components/StoryForm';
import StoryDisplay from '../components/story/StoryDisplay';
import { StoryResponse } from '../types/story';
import { StoryService } from '../services/StoryService';
import { StoryImageService } from '../services/StoryImageService';
import { useAuth } from '../contexts/AuthContext';
import AppLayout from '../components/layout/AppLayout';
import { logger } from '../utils/logger';
import { getRandomTip, getRandomTipByCategory, StoryTip } from '../utils/storyTips';
import '../styles/Pages.css';

/**
 * CreateStory page component
 */
const CreateStory: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedStory, setGeneratedStory] = useState<StoryResponse | null>(null);
  const [storyFormData, setStoryFormData] = useState<StoryFormData | null>(null);
  
  // Enhanced loading state
  const [loadingMessage, setLoadingMessage] = useState<string>('Creating Your Magical Story');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [currentTip, setCurrentTip] = useState<StoryTip | null>(null);
  const [loadingPhase, setLoadingPhase] = useState<string>('planning');
  
  // Refs to track intervals
  const progressIntervalRef = useRef<number | null>(null);
  const tipIntervalRef = useRef<number | null>(null);
  const phaseIntervalRef = useRef<number | null>(null);
  
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();
  
  // Clean up intervals when component unmounts
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) window.clearInterval(progressIntervalRef.current);
      if (tipIntervalRef.current) window.clearInterval(tipIntervalRef.current);
      if (phaseIntervalRef.current) window.clearInterval(phaseIntervalRef.current);
    };
  }, []);

  // Calculate estimated time based on story length
  const getEstimatedTime = (length: string): string => {
    switch (length) {
      case 'short': return '30-45 seconds';
      case 'long': return '1-2 minutes';
      default: return '45-60 seconds';
    }
  };

  // Calculate progress duration based on story length
  const getProgressDuration = (length: string): number => {
    switch (length) {
      case 'short': return 35000; // 35 seconds
      case 'long': return 100000; // 100 seconds (1 min 40s)
      default: return 60000; // 60 seconds for medium
    }
  };

  // Update loading phases to show different messages during generation
  const startPhaseUpdates = () => {
    // Define phases and their timing percentages
    const phases = [
      { phase: 'planning', message: 'Creating Your Magical Story', until: 20 },
      { phase: 'crafting', message: 'Crafting the storyline...', until: 40 },
      { phase: 'detailing', message: 'Adding magical details...', until: 60 },
      { phase: 'refining', message: 'Perfecting the tale...', until: 80 },
      { phase: 'finalizing', message: 'Almost ready...', until: 97 }
    ];
    
    setLoadingPhase('planning');
    
    // Clear any existing interval
    if (phaseIntervalRef.current) {
      window.clearInterval(phaseIntervalRef.current);
    }
    
    // Check progress and update phase accordingly
    const intervalId = window.setInterval(() => {
      const currentProgress = loadingProgress;
      
      for (let i = phases.length - 1; i >= 0; i--) {
        if (currentProgress >= phases[i].until) {
          if (loadingPhase !== phases[i + 1]?.phase && phases[i + 1]) {
            setLoadingPhase(phases[i + 1].phase);
            setLoadingMessage(phases[i + 1].message);
            
            // Get a tip relevant to the current phase
            const categories: Record<string, StoryTip['category']> = {
              planning: 'fact',
              crafting: 'tip',
              detailing: 'child',
              refining: 'app',
              finalizing: 'bedtime'
            };
            
            setCurrentTip(getRandomTipByCategory(categories[phases[i + 1].phase] || 'fact'));
          }
          break;
        }
      }
    }, 1000);
    
    phaseIntervalRef.current = intervalId;
    return intervalId;
  };

  // Start progress animation with non-linear progression
  const startProgressAnimation = (duration: number) => {
    // Reset progress
    setLoadingProgress(0);
    
    const startTime = Date.now();
    
    // Clear any existing interval
    if (progressIntervalRef.current) {
      window.clearInterval(progressIntervalRef.current);
    }
    
    // Start a new interval to update progress with non-linear progression
    const intervalId = window.setInterval(() => {
      const elapsed = Date.now() - startTime;
      const rawProgress = (elapsed / duration) * 100;
      
      // Apply easing function to make progress feel more natural
      // Start slower, speed up in the middle, slow down at the end
      let easedProgress;
      if (rawProgress < 30) {
        // Start slower
        easedProgress = rawProgress * 0.7;
      } else if (rawProgress < 70) {
        // Faster in the middle
        easedProgress = 21 + (rawProgress - 30) * 1.3;
      } else {
        // Slower at the end
        easedProgress = 73 + (rawProgress - 70) * 0.8;
      }
      
      const progress = Math.min(easedProgress, 97); // Max at 97% until complete
      setLoadingProgress(progress);
      
      if (progress >= 97) {
        window.clearInterval(intervalId);
      }
    }, 100);
    
    progressIntervalRef.current = intervalId;
    return intervalId;
  };
  
  // Start tip rotation with varied timing
  const startTipRotation = () => {
    // Set initial tip
    setCurrentTip(getRandomTip());
    
    // Clear any existing interval
    if (tipIntervalRef.current) {
      window.clearInterval(tipIntervalRef.current);
    }
    
    // Start a new interval to rotate tips with random timing between 8-12 seconds
    // This creates a more organic feel
    const rotateTip = () => {
      setCurrentTip(prevTip => {
        // Get a new tip that's different from the current one
        let newTip;
        do {
          newTip = getRandomTip();
        } while (newTip.id === prevTip?.id);
        return newTip;
      });
      
      // Set the next rotation after a random interval
      const nextInterval = 8000 + Math.random() * 4000; // 8-12 seconds
      tipIntervalRef.current = window.setTimeout(rotateTip, nextInterval) as unknown as number;
    };
    
    // Start the first rotation after 8 seconds
    tipIntervalRef.current = window.setTimeout(rotateTip, 8000) as unknown as number;
    return tipIntervalRef.current;
  };

  // Handle form submission to generate a story
  const handleStoryFormSubmit = async (formData: StoryFormData) => {
    setLoading(true);
    setError(null);
    setStoryFormData(formData);
    
    // Start progress animation and tip rotation
    startProgressAnimation(getProgressDuration(formData.length));
    startTipRotation();
    startPhaseUpdates();
    
    try {
      logger.log('Generating story with form data:', formData);
      const token = await getAccessToken();
      
      // Call the service to generate a story
      const response = await StoryService.generateStory(formData, token);
      logger.log('Story generation response:', response);
      
      if (response.id) {
        // If image generation is requested, start that process
        if (formData.generateImages) {
          try {
            logger.log('Starting image generation for story:', response.id);
            
            // Update loading message to indicate image generation
            setLoadingMessage('Story created! Starting AI image generation in background...');
            
            // Fire and forget - don't await the image generation
            StoryImageService.requestImageGeneration(
              response.id,
              response.text,
              formData.characters,
              formData.length
            ).catch(imageError => {
              logger.error('Error requesting image generation:', imageError);
              // Log error but don't block the user experience
            });
            
            logger.log('Image generation requested in background');
          } catch (imageError) {
            logger.error('Error initiating image generation:', imageError);
            // Continue even if image generation fails - we'll still show the story
          }
        }
        
        // Set progress to 100% to finish the bar
        setLoadingProgress(100);
        
        // Save the success state
        setLoadingMessage('Story created successfully! Redirecting you now...');
        
        // Wait a bit so the user sees the success message
        setTimeout(() => {
          navigate(`/story/${response.id}`);
        }, 1500);
      } else {
        logger.error('Warning: Story generated but no ID returned.');
        setError('Story was generated but cannot be viewed. Please check your stories page.');
      }
    } catch (err) {
      logger.error('Error generating story:', err);
      setError(err instanceof Error ? err.message : 'An error occurred while generating your story.');
    } finally {
      // Clean up intervals
      if (progressIntervalRef.current) {
        window.clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      
      if (tipIntervalRef.current) {
        window.clearTimeout(tipIntervalRef.current);
        tipIntervalRef.current = null;
      }
      
      if (phaseIntervalRef.current) {
        window.clearInterval(phaseIntervalRef.current);
        phaseIntervalRef.current = null;
      }
      
      setLoading(false);
    }
  };

  // Handle returning to the form to create another story
  const handleBackToForm = () => {
    setGeneratedStory(null);
  };
  
  // Handle successful story save/update
  const handleStoryUpdate = (updatedStory: StoryResponse) => {
    setGeneratedStory(updatedStory);
    
    // Navigate to the story view if it has an ID
    if (updatedStory && updatedStory.id) {
      navigate(`/story/${updatedStory.id}`);
    }
  };

  // Get loading text based on current phase
  const getLoadingSubtext = () => {
    switch (loadingPhase) {
      case 'planning':
        return 'Our storytellers are gathering inspiration...';
      case 'crafting':
        return 'Weaving characters and setting together...';
      case 'detailing':
        return 'Adding colorful details to your story...';
      case 'refining':
        return 'Polishing the narrative for the perfect bedtime tale...';
      case 'finalizing':
        return 'Putting on the finishing touches...';
      default:
        return 'Our storytellers are crafting your personalized tale...';
    }
  };

  return (
    <div className="page-container create-story-page">
      {/* Enhanced loading overlay during story generation */}
      {loading && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="loading-spinner"></div>
            <div className="loading-message">{loadingMessage}</div>
            
            {loadingMessage !== 'Story created successfully! Redirecting you now...' && (
              <>
                <div className="loading-estimate">
                  This could take {storyFormData ? getEstimatedTime(storyFormData.length) : '45-60 seconds'} to generate
                </div>
                
                <div className="loading-progress-container">
                  <div 
                    className="loading-progress-bar" 
                    style={{ width: `${loadingProgress}%` }}
                  ></div>
                </div>
                
                <div className="loading-progress-text">
                  {getLoadingSubtext()}
                </div>
                
                {currentTip && (
                  <div className="loading-tip">
                    <div className="tip-label">Did you know?</div>
                    <div className="tip-content">{currentTip.text}</div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      
      {generatedStory ? (
        // Show the story if one has been generated but no ID (fallback)
        <StoryDisplay 
          storyText={generatedStory.text}
          audioUrl={generatedStory.audioUrl}
          onBack={handleBackToForm}
          storyMetadata={storyFormData ?? undefined}
          title={generatedStory.title}
          storyId={generatedStory.id}
          onSaveSuccess={handleStoryUpdate}
          characters={storyFormData?.characters}
        />
      ) : (
        <div className="create-story-container">
          <div className="create-story-header">
            <h1 className="page-title">Create a New Story</h1>
            <p className="page-description">
              Design a custom bedtime story tailored to your child's interests and preferences.
              Fill out the form below, and our AI will craft a unique tale just for them!
            </p>
          </div>
          <StoryForm 
            onSubmit={handleStoryFormSubmit}
            isLoading={loading}
          />
        </div>
      )}
    </div>
  );
};

export default CreateStory; 